import type { Passkey } from '@/types/passkey';
import type { AxiosInstance, AxiosResponse } from 'axios';

// ── WebAuthn helpers ────────────────────────────────────────────────────────

/** Decode a base64url string to an ArrayBuffer (needed for WebAuthn challenges). */
function base64urlToBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return buffer;
}

/** Encode an ArrayBuffer to a base64url string (for sending to the server). */
function bufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/** Prepare the PublicKeyCredentialCreationOptions from the server response for the browser API. */
function prepareCreationOptions(options: any): PublicKeyCredentialCreationOptions {
    return {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        user: {
            ...options.user,
            id: base64urlToBuffer(options.user.id),
        },
        excludeCredentials: (options.excludeCredentials ?? []).map((c: any) => ({
            ...c,
            id: base64urlToBuffer(c.id),
        })),
    };
}

/** Prepare the PublicKeyCredentialRequestOptions from the server response for the browser API. */
function prepareRequestOptions(options: any): PublicKeyCredentialRequestOptions {
    return {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials ?? []).map((c: any) => ({
            ...c,
            id: base64urlToBuffer(c.id),
        })),
    };
}

/** Serialize a PublicKeyCredential for registration to JSON (for the server). */
function serializeRegistrationCredential(credential: PublicKeyCredential): object {
    const response = credential.response as AuthenticatorAttestationResponse;
    return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
            clientDataJSON: bufferToBase64url(response.clientDataJSON),
            attestationObject: bufferToBase64url(response.attestationObject),
        },
    };
}

/** Serialize a PublicKeyCredential for authentication to JSON (for the server). */
function serializeAuthenticationCredential(credential: PublicKeyCredential): object {
    const response = credential.response as AuthenticatorAssertionResponse;
    return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
            clientDataJSON: bufferToBase64url(response.clientDataJSON),
            authenticatorData: bufferToBase64url(response.authenticatorData),
            signature: bufferToBase64url(response.signature),
            userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
        },
    };
}

// ── PasskeyService ──────────────────────────────────────────────────────────

class PasskeyService {
    private readonly apiService: AxiosInstance;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    /**
     * Fetch registration options from the server and call navigator.credentials.create().
     * The `name` parameter is required by laravel/passkeys and shown in the passkey list.
     */
    async registerPasskey(name: string): Promise<AxiosResponse> {
        const optionsRes = await this.apiService.get('/user/passkeys/options');
        const creationOptions = prepareCreationOptions(optionsRes.data.options);

        const credential = (await navigator.credentials.create({
            publicKey: creationOptions,
        })) as PublicKeyCredential;

        return this.apiService.post('/user/passkeys', {
            name,
            credential: serializeRegistrationCredential(credential),
        });
    }

    /** Fetch login options from the server and call navigator.credentials.get(). */
    async loginWithPasskey(): Promise<AxiosResponse> {
        const optionsRes = await this.apiService.get('/passkeys/login/options');
        const requestOptions = prepareRequestOptions(optionsRes.data.options);

        const credential = (await navigator.credentials.get({
            publicKey: requestOptions,
        })) as PublicKeyCredential;

        return this.apiService.post('/passkeys/login', {
            credential: serializeAuthenticationCredential(credential),
        });
    }

    /** Raw: get registration options only (for testing / advanced use). */
    getOptions(): Promise<AxiosResponse> {
        return this.apiService.get('/user/passkeys/options');
    }

    /** Raw: register a passkey credential (for testing / advanced use). */
    register(name: string, credential: object): Promise<AxiosResponse> {
        return this.apiService.post('/user/passkeys', { name, credential });
    }

    /** Raw: get login options only (for testing / advanced use). */
    getLoginOptions(): Promise<AxiosResponse> {
        return this.apiService.get('/passkeys/login/options');
    }

    /** Raw: login with a passkey credential (for testing / advanced use). */
    login(credential: object): Promise<AxiosResponse> {
        return this.apiService.post('/passkeys/login', { credential });
    }

    /** List all registered passkeys for the authenticated user. */
    list(): Promise<AxiosResponse<{ data: Passkey[] }>> {
        return this.apiService.get('/user/passkeys');
    }

    /** Delete a passkey by id. */
    remove(id: string | number): Promise<AxiosResponse> {
        return this.apiService.delete(`/user/passkeys/${id}`);
    }
}

export default PasskeyService;
